// Care Staff overview, Console V4 (handoff "Care Staff overview rebuild"): the writes and
// reads beyond GET /overview/shift. Back end: src/aithora/v3/overview/shift-v4.ts and the
// V4 additions in shift.ts. Every write takes a client-generated id, so a queued tap that
// is replayed never writes twice; via VOICE marks a device-assistant write.
import { route } from './route';
import type { CareVitalsRecorded } from './residents-types';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

export type ShiftVia = 'TAP' | 'VOICE';
export interface LastReading { value: string; at: string | null }

/** GET /residents/:id/vitals/last: "Use last reading", the newest value of each reading (null when none). */
export const vitalsLast = route<{ real: boolean; residentId: string; bp: LastReading | null; hr: LastReading | null; temp: LastReading | null; spo2: LastReading | null; reason: string | null }>()(
  'GET', `${A}/residents/:id/vitals/last`, { honest: true });

/** POST /overview/shift/:taskId/outcome (V4 body): the alert check answers 409 with `gate` until overrideReason is sent. */
export const shiftOutcomeV4 = route<{ real: true; taskId: string; outcome: string; pagedNurse: boolean; overrideRecorded?: boolean; replayed?: boolean }, void, {
  outcome: string; vitals?: { bp?: string; hr?: string; temp?: string; spo2?: string }; flagToNurse?: boolean; clientId?: string; via?: ShiftVia; overrideReason?: string;
}>()('POST', `${A}/overview/shift/:taskId/outcome`, { honest: true });

/** POST /tasks/:id/offer {to, clientId}: "Can't get to it", offer my stop to a coworker on shift. */
export const taskOffer = route<{ real: true; offerId: string; taskId: string; to: { userId: string; name: string }; status: 'offered'; offeredAt: string; notified: boolean }, void, { to: string; clientId: string; via?: ShiftVia }>()(
  'POST', `${A}/tasks/:id/offer`, { honest: true });

/** POST /tasks/:id/offer/cancel {offerId}: Undo an offer still waiting. */
export const taskOfferCancel = route<{ real: true; offerId: string; status: 'cancelled' }, void, { offerId: string }>()('POST', `${A}/tasks/:id/offer/cancel`, { honest: true });

/** POST /tasks/:id/accept {offerId}: the coworker takes the stop. */
export const taskAccept = route<{ real: true; offerId: string; taskId: string; status: 'accepted'; acceptedAt: string | null }, void, { offerId: string; via?: ShiftVia }>()(
  'POST', `${A}/tasks/:id/accept`, { honest: true });

/** POST /overview/shift/signoff {clientId, notes}: Sign and hand off (shift_handoff, sealed to the Truth Ledger). */
export const shiftSignoff = route<{ real: true; id: string; next: string; unit: string | null; shift: string; signed_by: string; signed_at: string; open_ids: string[]; passed_ids: string[]; notes: string[] }, void, { clientId: string; notes: string[]; via?: ShiftVia }>()(
  'POST', `${A}/overview/shift/signoff`, { honest: true });

/** POST /overview/shift/signoff/reopen {id}: Reopen my sign-off. */
export const shiftSignoffReopen = route<{ real: true; id: string; reopenedAt: string | null }, void, { id: string }>()('POST', `${A}/overview/shift/signoff/reopen`, { honest: true });

/** The readings POST /care/vitals takes; any subset, blank ones are not recorded. */
export interface CareVitalsReadings { bpSystolic?: number; bpDiastolic?: number; heartRate?: number; tempF?: number; spo2?: number }

/**
 * POST /care/vitals {residentId, readings}: a bedside reading charted by hand, into the same
 * table as the vitals stops (care_vitals), so hold rules, trends, "Use last reading" and the
 * hall board all see it. The server decides what is out of range (outOfRange: bp, hr, temp,
 * spo2) and marks an abnormal reading flagged_to_nurse on the row; a flagged reading becomes one
 * worklist task for the charge nurse on shift (nurseTask; one open task per resident until it is
 * done). Care Staff for a resident assigned to them this shift; the building's Facility Admin and
 * the charge nurse on shift for any resident of their building (403 otherwise).
 */
export const careVitalsRecord = route<CareVitalsRecorded, void, { residentId: string; readings: CareVitalsReadings; note?: string }>()(
  'POST', `${A}/care/vitals`);

export const careShiftApi = { vitalsLast, shiftOutcomeV4, taskOffer, taskOfferCancel, taskAccept, shiftSignoff, shiftSignoffReopen, careVitalsRecord };
